import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Paintbrush, Loader2, Save, RotateCcw, Globe, Palette, Mail, Eye } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function WhiteLabelSettings({ token }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    platform_name: '', logo_url: '', logo_dark_url: '', favicon_url: '',
    primary_color: '', accent_color: '', header_bg: '',
    custom_domain: '', hide_lodgiflow_branding: false,
    custom_login_message: '', custom_footer_text: '',
    custom_support_email: '', custom_support_url: '',
    email_from_name: '', email_logo_url: '',
    hide_pricing_page: false, hide_marketing_site: false,
  });
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND}/api/admin/whitelabel`, { headers })
      .then(r => { setConfig(r.data); if (r.data.configured) setForm(prev => ({ ...prev, ...r.data })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${BACKEND}/api/admin/whitelabel`, form, { headers }); toast.success('White-label settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset all white-label settings to defaults?')) return;
    try { await axios.delete(`${BACKEND}/api/admin/whitelabel`, { headers }); toast.success('Reset to defaults'); setForm({ platform_name: '', logo_url: '', logo_dark_url: '', favicon_url: '', primary_color: '', accent_color: '', header_bg: '', custom_domain: '', hide_lodgiflow_branding: false, custom_login_message: '', custom_footer_text: '', custom_support_email: '', custom_support_url: '', email_from_name: '', email_logo_url: '', hide_pricing_page: false, hide_marketing_site: false }); setConfig({ configured: false }); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6" data-testid="whitelabel-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>White-Label & Branding</h2>
          <p className="text-sm text-muted-foreground">Customize LodgiFlow with your company's branding, colors, and domain.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reset} className="gap-1 text-xs"><RotateCcw className="h-3 w-3" /> Reset</Button>
          <Button size="sm" onClick={save} disabled={saving} className="gap-1 text-xs"><Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save'}</Button>
        </div>
      </div>

      {/* Preview */}
      <div className="p-4 rounded-xl border bg-gradient-to-r from-muted/30 to-muted/10">
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Preview</p>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-card border" style={{ backgroundColor: form.header_bg || undefined }}>
          {form.logo_url ? <img src={form.logo_url} alt="" className="h-6 w-6 rounded" onError={e => e.target.style.display='none'} /> : <Paintbrush className="h-5 w-5" style={{ color: form.primary_color || '#4F6DF5' }} />}
          <span className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk', color: form.primary_color || undefined }}>{form.platform_name || 'LodgiFlow'}</span>
          {form.primary_color && <div className="ml-auto flex gap-1"><div className="w-4 h-4 rounded" style={{ backgroundColor: form.primary_color }} />{form.accent_color && <div className="w-4 h-4 rounded" style={{ backgroundColor: form.accent_color }} />}</div>}
        </div>
      </div>

      {/* Branding */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Paintbrush className="h-4 w-4" /> Branding</h3>
        <div><Label className="text-xs">Platform Name</Label><Input value={form.platform_name} onChange={e => setForm(p => ({ ...p, platform_name: e.target.value }))} placeholder="LodgiFlow (leave blank for default)" className="mt-1" data-testid="wl-platform-name" /><p className="text-[10px] text-muted-foreground mt-0.5">Replaces "LodgiFlow" throughout the app for your users</p></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Logo URL (light bg)</Label><Input value={form.logo_url} onChange={e => setForm(p => ({ ...p, logo_url: e.target.value }))} placeholder="https://..." className="mt-1 text-xs" /></div>
          <div><Label className="text-xs">Logo URL (dark bg)</Label><Input value={form.logo_dark_url} onChange={e => setForm(p => ({ ...p, logo_dark_url: e.target.value }))} placeholder="https://..." className="mt-1 text-xs" /></div>
        </div>
        <div><Label className="text-xs">Favicon URL</Label><Input value={form.favicon_url} onChange={e => setForm(p => ({ ...p, favicon_url: e.target.value }))} placeholder="https://... (.ico or .png)" className="mt-1 text-xs" /></div>
      </div>

      <Separator />

      {/* Colors */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Palette className="h-4 w-4" /> Colors</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Primary Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.primary_color || '#4F6DF5'} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} className="h-8 w-8 rounded border cursor-pointer" />
              <Input value={form.primary_color} onChange={e => setForm(p => ({ ...p, primary_color: e.target.value }))} placeholder="#4F6DF5" className="text-xs font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Accent Color</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.accent_color || '#6C5CE7'} onChange={e => setForm(p => ({ ...p, accent_color: e.target.value }))} className="h-8 w-8 rounded border cursor-pointer" />
              <Input value={form.accent_color} onChange={e => setForm(p => ({ ...p, accent_color: e.target.value }))} placeholder="#6C5CE7" className="text-xs font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Header Background</Label>
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={form.header_bg || '#FFFFFF'} onChange={e => setForm(p => ({ ...p, header_bg: e.target.value }))} className="h-8 w-8 rounded border cursor-pointer" />
              <Input value={form.header_bg} onChange={e => setForm(p => ({ ...p, header_bg: e.target.value }))} placeholder="#FFFFFF" className="text-xs font-mono" />
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Domain */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Globe className="h-4 w-4" /> Custom Domain</h3>
        <div><Label className="text-xs">Custom Domain</Label><Input value={form.custom_domain} onChange={e => setForm(p => ({ ...p, custom_domain: e.target.value }))} placeholder="tech.coastalhospitality.com" className="mt-1" /><p className="text-[10px] text-muted-foreground mt-0.5">Point a CNAME record to lodgiflow.com. Contact support to verify.</p></div>
      </div>

      <Separator />

      {/* UI Overrides */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Eye className="h-4 w-4" /> UI Overrides</h3>
        <div><Label className="text-xs">Login Page Message</Label><Input value={form.custom_login_message} onChange={e => setForm(p => ({ ...p, custom_login_message: e.target.value }))} placeholder="Welcome to Coastal's Technology Hub" className="mt-1" /></div>
        <div><Label className="text-xs">Footer Text</Label><Input value={form.custom_footer_text} onChange={e => setForm(p => ({ ...p, custom_footer_text: e.target.value }))} placeholder="© 2026 Coastal Hospitality Group" className="mt-1" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Support Email</Label><Input value={form.custom_support_email} onChange={e => setForm(p => ({ ...p, custom_support_email: e.target.value }))} placeholder="it@coastalhospitality.com" className="mt-1 text-xs" /></div>
          <div><Label className="text-xs">Support URL</Label><Input value={form.custom_support_url} onChange={e => setForm(p => ({ ...p, custom_support_url: e.target.value }))} placeholder="https://help.coastalhospitality.com" className="mt-1 text-xs" /></div>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg border"><div><span className="text-sm font-medium">Hide LodgiFlow Branding</span><p className="text-[10px] text-muted-foreground">Remove "Powered by LodgiFlow" and LodgiFlow references</p></div><Switch checked={form.hide_lodgiflow_branding} onCheckedChange={v => setForm(p => ({ ...p, hide_lodgiflow_branding: v }))} /></div>
        <div className="flex items-center justify-between p-3 rounded-lg border"><div><span className="text-sm font-medium">Hide Pricing Page</span><p className="text-[10px] text-muted-foreground">Remove pricing page access for your white-labeled instance</p></div><Switch checked={form.hide_pricing_page} onCheckedChange={v => setForm(p => ({ ...p, hide_pricing_page: v }))} /></div>
      </div>

      <Separator />

      {/* Email Branding */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold flex items-center gap-2"><Mail className="h-4 w-4" /> Email Branding</h3>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Email "From" Name</Label><Input value={form.email_from_name} onChange={e => setForm(p => ({ ...p, email_from_name: e.target.value }))} placeholder="Coastal Tech Hub" className="mt-1" /></div>
          <div><Label className="text-xs">Email Logo URL</Label><Input value={form.email_logo_url} onChange={e => setForm(p => ({ ...p, email_logo_url: e.target.value }))} placeholder="https://..." className="mt-1 text-xs" /></div>
        </div>
      </div>
    </div>
  );
}
